// 우측 상단 종 아이콘 + 드롭다운.
// 읽지 않음 수 배지, 최근 10건, 전체 읽음 버튼.
// 아이콘: lucide-react Bell (U34 아이콘 시스템 도입)

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import {
  useInboxList,
  useMarkAllRead,
  useMarkRead,
  useUnreadCount,
} from '../hooks/useNotificationsInbox';
import type { NotificationPublic, NotifKind } from '../api/notifications';

const MAX_BADGE = 99;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const countQ = useUnreadCount();
  const listQ = useInboxList({ limit: 10 });
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  // 바깥 클릭으로 드롭다운 닫기
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const count = countQ.data ?? 0;
  const items = listQ.data?.items ?? [];

  const onItemClick = async (n: NotificationPublic) => {
    if (!n.isRead) {
      try { await markRead.mutateAsync(n.id); } catch { /* 무시 */ }
    }
    setOpen(false);
    // U32: v1 /credentials 경로 제거 — Explorer 루트로 이동
    if (n.credentialId !== null) navigate('/');
  };

  const onMarkAll = async () => {
    try { await markAll.mutateAsync(); } catch { /* 무시 */ }
  };

  return (
    <div ref={ref} className="bell">
      <button
        type="button"
        className="bell__btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={`알림 ${count}건`}
      >
        {/* 🔔 이모지 → lucide Bell 아이콘 (U34) */}
        <Bell size={16} aria-hidden />
        {count > 0 && (
          <span className="bell__badge">{count > MAX_BADGE ? `${MAX_BADGE}+` : count}</span>
        )}
      </button>
      {open && (
        <div className="bell__dropdown">
          <header className="bell__header">
            <strong>알림</strong>
            {count > 0 && (
              <button type="button" className="btn btn--tiny" onClick={onMarkAll} disabled={markAll.isPending}>
                전체 읽음
              </button>
            )}
          </header>
          <div className="bell__list">
            {items.length === 0 ? (
              <div className="bell__empty muted">알림이 없습니다</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`bell__item ${n.isRead ? 'read' : 'unread'} kind-${n.type}`}
                  onClick={() => onItemClick(n)}
                >
                  <div className="bell__item-head">
                    <span className="bell__kind">{kindLabel(n.type)}</span>
                    <span className="bell__time">{relativeTime(n.createdAt)}</span>
                  </div>
                  <div className="bell__title">{n.title}</div>
                  <div className="bell__msg">{n.message}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function kindLabel(k: NotifKind): string {
  switch (k) {
    case 'security': return '보안';
    case 'schedule': return '만료';
    case 'system': return '시스템';
  }
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return '방금';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}분 전`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}시간 전`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days}일 전`;
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
